/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { noop, type Unsubscribe } from '@amazon/vinyl-util'
import type { Auth, AuthStatus } from './auth'

export interface AuthScopedMemo<T> {
    /** Reads through the memo, fetching once per auth status. */
    readonly get: () => Promise<T>
    /** Drops the current memo, so the next `get` re-reads. */
    readonly reset: () => void
}

/**
 * Memoizes a per-account read for as long as the auth status it was taken under
 * still holds. A sign-out drops it, and so does a sign-IN — otherwise an
 * anonymous result, which carries no entitlements, outlives the sign-in that
 * should have replaced it. A rejection is never cached, so one transient failure
 * can't wedge the gate it feeds for the rest of the session.
 *
 * This exists because the hand-rolled version of it was wrong in the same way in
 * four places: `onData` invokes its callback synchronously during subscribe, so
 * `const sub = status.onData(() => sub())` throws `ReferenceError: Cannot access
 * 'sub' before initialization` whenever the very first emit already matches.
 */
export function createAuthScopedMemo<T>(
    getAuth: () => Pick<Auth, 'status'>,
    read: () => Promise<T>
): AuthScopedMemo<T> {
    let pending: Promise<T> | null = null
    let pendingUnder: AuthStatus | undefined
    let dropCurrent: () => void = noop

    function get(): Promise<T> {
        const status$ = getAuth().status
        const readUnder = status$.value
        // Status listeners subscribed before this memo's fire first; they must not get the stale read.
        if (pending && pendingUnder === readUnder) return pending
        dropCurrent()

        let unsubscribe: Unsubscribe = noop
        let live = true
        // Scoped to THIS read, so a late invalidation can never drop a newer memo.
        const invalidate = (): void => {
            if (!live) return
            live = false
            pending = null
            unsubscribe()
        }
        dropCurrent = invalidate

        pendingUnder = readUnder
        pending = read().catch((err: unknown) => {
            invalidate()
            throw err
        })
        unsubscribe = status$.onData((status) => {
            if (status !== readUnder) invalidate()
        })
        return pending
    }

    return { get, reset: () => dropCurrent() }
}
